import { Input } from '../../../../shared/components/FormControls';
import Rodal from 'rodal';
import { Button } from '../../../../shared/components/Button';
import '../../../../shared/styles/Orchestrator.css';
import 'rodal/lib/rodal.css';
import { ArrowUp, ArrowDown, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

function EditScenesModal({
  visible,
  scenes = [],
  onCancel,
  onSave,
  onDeleteScene,
  mode = 'light',
}) {
  const isDark = mode === 'dark';
  const [localScenes, setLocalScenes] = useState([]);
  const [isPhone, setIsPhone] = useState(false);

  useEffect(() => {
    const mediaQueryWidth = window.matchMedia('(max-width: 620px)');
    const handleMediaChange = () => setIsPhone(mediaQueryWidth.matches);
    handleMediaChange();
    mediaQueryWidth.addEventListener('change', handleMediaChange);
    return () => mediaQueryWidth.removeEventListener('change', handleMediaChange);
  }, []);

  useEffect(() => {
    if (visible) {
      setLocalScenes(scenes.map((s) => ({ id: s.id, name: s.name || '' })));
    }
  }, [visible, scenes]);

  const updateName = (id, name) => {
    setLocalScenes((prev) =>
      prev.map((s) => (s.id === id ? { ...s, name } : s))
    );
  };

  const moveUp = (index) => {
    if (index <= 0) return;
    setLocalScenes((prev) => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
  };

  const moveDown = (index) => {
    setLocalScenes((prev) => {
      if (index >= prev.length - 1) return prev;
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
  };

  const handleSave = () => {
    onSave(localScenes);
  };

  const borderColor = isDark ? 'var(--onyx)' : '#e4E7C5';
  const bg = isDark ? 'var(--jet)' : '#f6f7eb';
  const textColor = isDark ? 'var(--ivory)' : 'var(--jet)';
  const inputBorder = isDark ? '1px solid #4a4f52' : '1px solid #ccc';
  const inputBg = isDark ? 'var(--onyx)' : '#fff';

  return (
    <Rodal
      animation='slideUp'
      visible={visible}
      onClose={onCancel}
      className={isDark ? 'df_rodal_dark' : ''}
      customStyles={{
        border: `2px solid ${borderColor}`,
        backgroundColor: bg,
        color: textColor,
        width: isPhone ? 'calc(100% - 34px)' : 480,
        height: isPhone ? 'calc(100% - 114px)' : Math.min(80 + localScenes.length * 52 + 80, 560),
        marginTop: isPhone ? '80px' : '',
      }}
    >
      <div
        className={`trialment_login_form ${isDark ? 'df_modal_form_dark' : ''}`}
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <div
          className='trialment_login_form_row'
          style={{ fontWeight: 700, fontSize: 16, marginBottom: 6, textAlign: 'center' }}
        >
          Edit Screens
        </div>

        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            paddingRight: '4px',
          }}
        >
          {localScenes.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '20px',
                fontSize: '14px',
                color: isDark ? 'rgba(246,247,235,0.5)' : '#888',
              }}
            >
              No screens to edit.
            </div>
          ) : (
            localScenes.map((scene, index) => (
              <div
                key={scene.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 8px',
                  border: `1px solid ${isDark ? '#3a3f42' : '#d5d7c0'}`,
                  borderRadius: '4px',
                  background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                }}
              >
                <Input
                  type='text'
                  value={scene.name}
                  onChange={(e) => updateName(scene.id, e.target.value)}
                  style={{
                    flex: 1,
                    fontSize: '14px',
                    padding: '4px 8px',
                    border: inputBorder,
                    borderRadius: '4px',
                    background: inputBg,
                    color: textColor,
                    outline: 'none',
                    minWidth: 0,
                  }}
                />
                <button
                  onClick={() => moveUp(index)}
                  disabled={index === 0}
                  title='Move up'
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: index === 0 ? 'default' : 'pointer',
                    color: index === 0
                      ? isDark ? '#444' : '#ccc'
                      : isDark ? 'var(--ivory)' : 'var(--jet)',
                    padding: '2px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <ArrowUp size={16} />
                </button>
                <button
                  onClick={() => moveDown(index)}
                  disabled={index === localScenes.length - 1}
                  title='Move down'
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: index === localScenes.length - 1 ? 'default' : 'pointer',
                    color: index === localScenes.length - 1
                      ? isDark ? '#444' : '#ccc'
                      : isDark ? 'var(--ivory)' : 'var(--jet)',
                    padding: '2px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <ArrowDown size={16} />
                </button>
                <button
                  onClick={() => onDeleteScene(scene.id)}
                  title='Delete screen'
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#e94f37',
                    padding: '2px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))
          )}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            marginTop: '12px',
            marginBottom: '8px',
            flexShrink: 0,
          }}
        >
          <Button
            label='Cancel'
            type={isDark ? 'onyx' : 'black-white'}
            onPress={onCancel}
          />
          <Button label='Save' type='cinnabar' onPress={handleSave} />
        </div>
      </div>
    </Rodal>
  );
}

export default EditScenesModal;
